import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { API_BASE } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { FileSpreadsheet, FileText, Search, ChevronLeft, ChevronRight, X, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { parsearPasajerosDeServicio } from '../lib/pasajerosUtils';

const ITEMS_PER_PAGE = 10;

export const ReportesPage = () => {
  const { user } = useAuth();
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientes, setClientes] = useState([]);
  const [showValesModal, setShowValesModal] = useState(false);
  const [selectedClienteVales, setSelectedClienteVales] = useState('');
  const [exportingVales, setExportingVales] = useState(false);
  const [filters, setFilters] = useState({
    fecha_desde: '',
    fecha_hasta: '',
    centro_costo: 'all',
    cliente_id: 'all'
  });

  useEffect(() => {
    // Cargar centros de costo de la empresa del usuario (admin o cliente)
    if (user?.empresa_id) {
      loadCentrosCosto(user.empresa_id);
    }
    
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
      fecha_desde: firstDay.toISOString().split('T')[0],
      fecha_hasta: lastDay.toISOString().split('T')[0],
      centro_costo: 'all',
      cliente_id: 'all'
    });
  }, [user]);

  // Cargar clientes para el modal de vales
  useEffect(() => {
    const loadClientes = async () => {
      if (user?.role === 'admin') {
        try {
          const response = await api.get('/clientes');
          setClientes(response.data || []);
        } catch (error) {
          console.error('Error al cargar clientes');
        }
      }
    };
    loadClientes();
  }, [user]);

  const loadCentrosCosto = async (empresaId) => {
    try {
      // Obtener centros de costo de la empresa del usuario
      const response = await api.get(`/empresas/${empresaId}/centros-costo`);
      if (response.data && response.data.centros_costo) {
        setCentrosCosto(response.data.centros_costo);
      } else {
        setCentrosCosto([]);
      }
    } catch (error) {
      console.error('Error al cargar centros de costo');
      setCentrosCosto([]);
    }
  };

  // Filtrado por búsqueda de texto
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return datos;
    
    const term = searchTerm.toLowerCase();
    return datos.filter(item => 
      (item.folio && item.folio.toString().includes(term)) ||
      (item.cliente_nombre && item.cliente_nombre.toLowerCase().includes(term)) ||
      (item.pasajeros && item.pasajeros.toLowerCase().includes(term)) ||
      (item.origen && item.origen.toLowerCase().includes(term)) ||
      (item.destino && item.destino.toLowerCase().includes(term)) ||
      (item.centro_costo && item.centro_costo.toLowerCase().includes(term)) ||
      (item.fecha && item.fecha.includes(term)) ||
      (item.estado && item.estado.toLowerCase().includes(term)) ||
      (item.usuario_solicitante && item.usuario_solicitante.toLowerCase().includes(term))
    );
  }, [datos, searchTerm]);

  // Cálculos para cuadros de mando (basados en datos filtrados)
  const stats = useMemo(() => {
    const agendados = filteredData.filter(item => item.estado === 'Agendado');
    const realizados = filteredData.filter(item => item.estado === 'Realizado');
    const suspendidos = filteredData.filter(item => item.estado === 'Suspendido');
    const totalFacturar = realizados.reduce((sum, item) => sum + item.tarifa, 0);
    
    return {
      total: filteredData.length,
      agendados: agendados.length,
      realizados: realizados.length,
      suspendidos: suspendidos.length,
      totalFacturar
    };
  }, [filteredData]);

  // Paginación (basada en datos filtrados)
  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  // Reset página cuando cambia el término de búsqueda
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleBuscar = async () => {
    if (!filters.fecha_desde || !filters.fecha_hasta) {
      toast.error('Debe seleccionar rango de fechas');
      return;
    }

    setLoading(true);
    setCurrentPage(1);
    setSearchTerm(''); // Limpiar búsqueda al hacer nueva consulta
    try {
      const params = {
        fecha_desde: filters.fecha_desde,
        fecha_hasta: filters.fecha_hasta
      };

      if (filters.centro_costo && filters.centro_costo !== 'all') {
        params.centro_costo = filters.centro_costo;
      }
      if (filters.cliente_id && filters.cliente_id !== 'all') {
        params.cliente_id = filters.cliente_id;
      }

      const response = await api.get('/reportes/cuadratura', { params });
      setDatos(response.data);
      toast.success(`${response.data.length} registros encontrados`);
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!filters.fecha_desde || !filters.fecha_hasta) {
      toast.error('Debe seleccionar rango de fechas');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        fecha_desde: filters.fecha_desde,
        fecha_hasta: filters.fecha_hasta
      });

      if (filters.centro_costo && filters.centro_costo !== 'all') {
        params.append('centro_costo', filters.centro_costo);
      }
      if (filters.cliente_id && filters.cliente_id !== 'all') {
        params.append('cliente_id', filters.cliente_id);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reportes/cuadratura/excel?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error al exportar');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuadratura_${filters.fecha_desde}_${filters.fecha_hasta}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Reporte Excel descargado');
    } catch (error) {
      toast.error('Error al exportar a Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!filters.fecha_desde || !filters.fecha_hasta) {
      toast.error('Debe seleccionar rango de fechas');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        fecha_desde: filters.fecha_desde,
        fecha_hasta: filters.fecha_hasta
      });
      
      if (filters.centro_costo && filters.centro_costo !== 'all') {
        params.append('centro_costo', filters.centro_costo);
      }
      if (filters.cliente_id && filters.cliente_id !== 'all') {
        params.append('cliente_id', filters.cliente_id);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reportes/cuadratura/pdf?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error al exportar');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuadratura_${filters.fecha_desde}_${filters.fecha_hasta}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Reporte PDF descargado');
    } catch (error) {
      toast.error('Error al exportar a PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleExportValesDigitales = async () => {
    if (!selectedClienteVales) {
      toast.error('Debe seleccionar un cliente');
      return;
    }

    if (!filters.fecha_desde || !filters.fecha_hasta) {
      toast.error('Debe seleccionar rango de fechas');
      return;
    }

    setExportingVales(true);
    try {
      const params = new URLSearchParams({
        fecha_desde: filters.fecha_desde,
        fecha_hasta: filters.fecha_hasta,
        cliente_id: selectedClienteVales
      });

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/reportes/vales-digitales?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        // Clonar el response para poder leerlo dos veces si es necesario
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.detail || 'Error al exportar');
        } catch (e) {
          throw new Error('No hay servicios realizados para este cliente en el período seleccionado');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const clienteNombre = clientes.find(c => c.id === selectedClienteVales)?.nombre || 'cliente';
      a.download = `vales_digitales_${clienteNombre}_${filters.fecha_desde}_${filters.fecha_hasta}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Vales Digitales exportados');
      setShowValesModal(false);
      setSelectedClienteVales('');
    } catch (error) {
      toast.error(error.message || 'Error al exportar vales digitales');
    } finally {
      setExportingVales(false);
    }
  };

  // Función para formatear fecha a DD-MM-AAAA
  const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="space-y-6" data-testid="reportes-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Reportes de Cuadratura</h1>
        <p className="text-gray-600 mt-1">Generación de reportes mensuales para facturación</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col">
              <Label htmlFor="fecha_desde" className="mb-1.5 text-xs">Fecha Desde</Label>
              <Input
                id="fecha_desde"
                type="date"
                value={filters.fecha_desde}
                onChange={(e) => setFilters({ ...filters, fecha_desde: e.target.value })}
                data-testid="fecha-desde-input"
                className="w-[150px] h-9"
              />
            </div>

            <div className="flex flex-col">
              <Label htmlFor="fecha_hasta" className="mb-1.5 text-xs">Fecha Hasta</Label>
              <Input
                id="fecha_hasta"
                type="date"
                value={filters.fecha_hasta}
                onChange={(e) => setFilters({ ...filters, fecha_hasta: e.target.value })}
                data-testid="fecha-hasta-input"
                className="w-[150px] h-9"
              />
            </div>

            {(user?.role === 'admin' || user?.role === 'superadmin') && clientes.length > 0 && (
              <div className="flex flex-col">
                <Label htmlFor="cliente_filter" className="mb-1.5 text-xs">Cliente</Label>
                <Select
                  value={filters.cliente_id || "all"}
                  onValueChange={(value) => setFilters({ ...filters, cliente_id: value })}
                >
                  <SelectTrigger id="cliente_filter" data-testid="cliente-filter-select" className="w-[180px] h-9">
                    <SelectValue placeholder="Todos los clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los clientes</SelectItem>
                    {clientes.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {centrosCosto.length > 0 && (
              <div className="flex flex-col">
                <Label htmlFor="centro_costo" className="mb-1.5 text-xs">Centro de Costo</Label>
                <Select
                  value={filters.centro_costo || "all"}
                  onValueChange={(value) => setFilters({ ...filters, centro_costo: value })}
                >
                  <SelectTrigger id="centro_costo" data-testid="centro-costo-filter-select" className="w-[170px] h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {centrosCosto.map(cc => (
                      <SelectItem key={cc} value={cc}>{cc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              onClick={handleBuscar}
              disabled={loading}
              data-testid="buscar-button"
              className="h-9"
            >
              <Search size={16} className="mr-1.5" />
              Buscar
            </Button>
            <Button
              onClick={handleExportExcel}
              disabled={loading || datos.length === 0}
              variant="outline"
              data-testid="export-excel-button"
              className="h-9"
            >
              <FileSpreadsheet size={16} className="mr-1" />
              Excel
            </Button>
            <Button
              onClick={handleExportPDF}
              disabled={loading || datos.length === 0}
              variant="outline"
              data-testid="export-pdf-button"
              className="h-9"
            >
              <FileText size={16} className="mr-1" />
              PDF
            </Button>
            {user?.role === 'admin' && (
              <Button
                onClick={() => setShowValesModal(true)}
                disabled={loading}
                variant="outline"
                className="h-9 bg-primary/10 hover:bg-primary/20 text-primary border-primary/30"
                data-testid="export-vales-button"
              >
                <Receipt size={16} className="mr-1" />
                Vales Digitales
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {datos.length > 0 && (
        <>
          {/* Buscador en tiempo real */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Buscar por cliente, pasajero, origen, destino, fecha..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10"
              data-testid="search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Indicador de resultados filtrados */}
          {searchTerm && (
            <div className="text-sm text-gray-500">
              Mostrando {filteredData.length} de {datos.length} registros para &ldquo;{searchTerm}&rdquo;
            </div>
          )}

          {/* Cuadros de Mando */}
          <div className="grid grid-cols-5 gap-2">
            <Card className="bg-gray-50 border-gray-200">
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.agendados}</div>
                <div className="text-xs text-blue-600">Agendados</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.realizados}</div>
                <div className="text-xs text-green-600">Realizados</div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-3 text-center">
                <div className="text-2xl font-bold text-red-600">{stats.suspendidos}</div>
                <div className="text-xs text-red-600">Suspendidos</div>
              </CardContent>
            </Card>
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-3 text-center">
                <div className="text-xl font-bold text-primary">${stats.totalFacturar.toLocaleString('es-CL')}</div>
                <div className="text-xs text-primary">A Facturar</div>
              </CardContent>
            </Card>
          </div>

          {/* Tabla de datos */}
          <Card>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Folio</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Hora</TableHead>
                      {user?.role === 'admin' && <TableHead>Cliente</TableHead>}
                      <TableHead>Creado por</TableHead>
                      <TableHead>Pasajero(s)</TableHead>
                      <TableHead>Centro Costo</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Destino</TableHead>
                      <TableHead>Tarifa</TableHead>
                      <TableHead>T. Espera</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Confirmado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedData.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono font-bold text-primary">
                          #{item.folio || '-'}
                        </TableCell>
                        <TableCell>{item.fecha}</TableCell>
                        <TableCell>{item.hora}</TableCell>
                        {user?.role === 'admin' && <TableCell>{item.cliente_nombre || '-'}</TableCell>}
                        <TableCell>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                            item.creado_por === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {item.creado_por === 'admin' ? 'Admin' : 'Cliente'}
                          </span>
                          <span className="block text-xs text-gray-500 mt-0.5">{item.usuario_solicitante}</span>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <span className="block truncate" title={item.pasajeros}>
                            {item.pasajeros || '-'}
                          </span>
                        </TableCell>
                        <TableCell>{item.centro_costo || '-'}</TableCell>
                        <TableCell className="max-w-[220px]">
                          {(() => {
                            const parsed = parsearPasajerosDeServicio({
                              observaciones: item.observaciones || '',
                              nombre_pasajero: item.nombre_pasajero || '',
                              origen: item.origen || '',
                              destino: item.destino || ''
                            });
                            const origenes = [...new Set(parsed.pasajeros.map(p => p.origen).filter(Boolean))];
                            if (parsed.pasajeros.length <= 1) {
                              return <span>{item.origen || '-'}</span>;
                            }
                            if (origenes.length === 1) {
                              return <span>{origenes[0]}</span>;
                            }
                            return (
                              <div className="flex flex-col gap-0.5 text-sm">
                                {origenes.map((o, i) => (
                                  <span key={i} className="truncate" title={o}>{i + 1}. {o}</span>
                                ))}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="max-w-[220px]">
                          {(() => {
                            const parsed = parsearPasajerosDeServicio({
                              observaciones: item.observaciones || '',
                              nombre_pasajero: item.nombre_pasajero || '',
                              origen: item.origen || '',
                              destino: item.destino || ''
                            });
                            const destinos = [...new Set(parsed.pasajeros.map(p => p.destino).filter(Boolean))];
                            if (parsed.pasajeros.length <= 1) {
                              return <span>{item.destino || '-'}</span>;
                            }
                            if (destinos.length === 1) {
                              return <span>{destinos[0]}</span>;
                            }
                            return (
                              <div className="flex flex-col gap-0.5 text-sm">
                                {destinos.map((d, i) => (
                                  <span key={i} className="truncate" title={d}>{i + 1}. {d}</span>
                                ))}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="font-bold text-primary">
                          ${item.tarifa.toLocaleString('es-CL')}
                        </TableCell>
                        <TableCell>
                          <span className={`text-sm ${item.tiempo_espera > 0 ? 'font-medium text-amber-700' : 'text-gray-400'}`}>
                            {item.tiempo_espera > 0 ? `${item.tiempo_espera} min` : '-'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                            item.estado === 'Realizado' ? 'bg-green-100 text-green-800' :
                            item.estado === 'Agendado' ? 'bg-blue-100 text-blue-800' :
                            item.estado === 'Suspendido' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.estado}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                            item.confirmado ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.confirmado ? 'Sí' : 'No'}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-600">
                    Mostrando {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredData.length)} de {filteredData.length} registros
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} className="mr-1" />
                      Anterior
                    </Button>
                    <span className="px-3 py-1 text-sm font-medium">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                      <ChevronRight size={16} className="ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Leyenda de estados */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                <div className="flex flex-wrap gap-4 items-center">
                  <span className="text-sm font-medium text-gray-700">Leyenda:</span>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full bg-blue-500"></span>
                    <span className="text-xs text-gray-600">Agendado</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-xs text-gray-600">Realizado (facturable)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-xs text-gray-600">Suspendido</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Modal para seleccionar cliente - Exportar Vales Digitales */}
      <Dialog open={showValesModal} onOpenChange={setShowValesModal}>
        <DialogContent className="max-w-md" data-testid="vales-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Receipt className="w-6 h-6 text-primary" />
              Exportar Vales Digitales
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <p className="text-gray-600">
              Seleccione el cliente para exportar todos sus servicios <strong>realizados</strong> en el período seleccionado.
              Cada servicio se mostrará en una página individual con todos sus detalles.
            </p>

            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Período:</span>
                <span className="font-medium">{formatDateDDMMYYYY(filters.fecha_desde)} a {formatDateDDMMYYYY(filters.fecha_hasta)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cliente-vales">Cliente *</Label>
              <Select
                value={selectedClienteVales}
                onValueChange={setSelectedClienteVales}
              >
                <SelectTrigger id="cliente-vales" data-testid="cliente-vales-select">
                  <SelectValue placeholder="Seleccionar cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowValesModal(false);
                setSelectedClienteVales('');
              }}
              disabled={exportingVales}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleExportValesDigitales}
              disabled={exportingVales || !selectedClienteVales}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-vales-button"
            >
              {exportingVales ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exportando...
                </>
              ) : (
                <>
                  <Receipt className="w-4 h-4 mr-2" />
                  Exportar PDF
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};